import { useEffect } from "react";

/**
 * Drives a CSS-variable based parallax on the supplied root element.
 * On every animation frame, eases the current --mx / --my towards the
 * cursor-derived target so children with `.parallax-layer` can read them.
 *
 * - Skips on coarse pointer devices.
 * - Cleans up listener + RAF on unmount.
 */
export function useCursorParallax(rootRef) {
  useEffect(() => {
    // Allow callers to opt out entirely by passing null (e.g. on touch
    // viewports). Without this guard the hook would throw on the first
    // `rootRef.current` access.
    if (!rootRef) return undefined;
    const root = rootRef.current;
    if (!root) return undefined;
    const canFine = window.matchMedia("(pointer: fine)").matches;
    if (!canFine) return undefined;

    let raf = 0;
    let running = false;
    let targetX = 0;
    let targetY = 0;
    let curX = 0;
    let curY = 0;

    const tick = () => {
      curX += (targetX - curX) * 0.06;
      curY += (targetY - curY) * 0.06;
      root.style.setProperty("--mx", curX.toFixed(3));
      root.style.setProperty("--my", curY.toFixed(3));
      // Idle-stop: once the eased position has converged on the target
      // there is nothing left to animate, so release the frame loop
      // instead of spinning at 60fps forever.
      if (Math.abs(targetX - curX) < 0.002 && Math.abs(targetY - curY) < 0.002) {
        running = false;
        return;
      }
      raf = requestAnimationFrame(tick);
    };

    const wake = () => {
      if (running) return;
      running = true;
      raf = requestAnimationFrame(tick);
    };

    const onMove = (e) => {
      const rect = root.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width - 0.5;
      const y = (e.clientY - rect.top) / rect.height - 0.5;
      targetX = Math.max(-1, Math.min(1, x * 2));
      targetY = Math.max(-1, Math.min(1, y * 2));
      wake();
    };

    wake();
    window.addEventListener("mousemove", onMove);

    return () => {
      window.removeEventListener("mousemove", onMove);
      cancelAnimationFrame(raf);
    };
  }, [rootRef]);
}
